import React, { useState, useEffect } from 'react';
import { Card, Badge, Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL, getBearerToken } from '../../../functions';

const UmrahPackageCard = ({ package: pkg, onBook }) => {
  const [airlineData, setAirlineData] = useState(null);
  const [airlineDataLoaded, setAirlineDataLoaded] = useState(false);
  const [logoLoadStates, setLogoLoadStates] = useState({});

  // Fetch airline data
  useEffect(() => {
    axios.get(`${API_BASE_URL}/general`, {
      headers: { Authorization: `Bearer ${getBearerToken()}` }
    }).then(response => {
      setAirlineData(response?.data?.data?.airline || []);
      setAirlineDataLoaded(true);
    }).catch(error => {
      console.error('Error fetching airline data:', error);
      setAirlineDataLoaded(true); // Set to true even on error to prevent infinite loading
    });
  }, []);

  // Function to get airline logo by ID
  const getAirlineLogo = (airlineId) => {
    if (!airlineData || !airlineId) return null;
    const airline = airlineData.find(item => item.id == airlineId);
    
    if (airline && airline.logo) {
      // Extract filename from the original logo URL
      const filename = airline.logo.split('/').pop();
      // Use the new API route for serving logos with CORS headers
      const apiLogoUrl = `${API_BASE_URL}/airline-logo/${filename}`;
      return apiLogoUrl;
    }
    
    return null;
  };

  // Function to get airline name by ID (fallback)
  const getAirlineName = (airlineId) => {
    if (!airlineData || !airlineId) return airlineId; // fallback to ID if no data
    const airline = airlineData.find(item => item.id == airlineId);
    return airline ? airline.full_name : airlineId; // fallback to ID if not found
  };

  // Function to handle logo load error
  const handleLogoError = (airlineId) => {
    setLogoLoadStates(prev => ({
      ...prev,
      [airlineId]: 'failed'
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[date.getMonth()];
    return `${day}-${month}`;
  };

  const calculateNights = () => {
    if (!pkg.departure_flights || !pkg.return_flights || pkg.departure_flights.length === 0 || pkg.return_flights.length === 0) {
      return 0;
    }
    
    const departureDate = pkg.departure_flights[0]?.departure_date;
    const returnDate = pkg.return_flights[0]?.arrival_date;
    
    if (!departureDate || !returnDate) return 0;
    
    const start = new Date(departureDate);
    const end = new Date(returnDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getLowestPrice = () => {
    if (!pkg.prices || pkg.prices.length === 0) return null;
    const allPrices = [];
    const sharingPrices = pkg.prices.filter(p => p.room_type === 'sharing');
    sharingPrices.forEach(p => {
      if (p.price_adult && p.price_adult > 0) allPrices.push(Number(p.price_adult));
      if (p.price_child && p.price_child > 0) allPrices.push(Number(p.price_child));
      if (p.price_infant && p.price_infant > 0) allPrices.push(Number(p.price_infant));
    });
    if (allPrices.length === 0) return null;
    return Math.min(...allPrices);
  };

  // Helper function to get flight info safely
  const getFlightInfo = (flights, type) => {
    if (!flights || flights.length === 0) return null;
    return flights[0];
  };

  // Helper function to get all flight segments for via flights
  const getAllFlightSegments = (flights) => {
    if (!flights || flights.length === 0) return [];
    return flights.sort((a, b) => a.segment_order - b.segment_order);
  };

  // Helper function to format flight route with via stops
  const formatFlightRoute = (flights) => {
    if (!flights || flights.length === 0) return 'Route not available';
    
    const segments = getAllFlightSegments(flights);
    if (segments.length === 1) {
      return `${segments[0].departure_airport} → ${segments[0].arrival_airport}`;
    } else {
      const route = segments.map(segment => segment.departure_airport).join(' → ');
      return `${route} → ${segments[segments.length - 1].arrival_airport}`;
    }
  };

  const getRoomCapacity = (roomType) => {
    switch(roomType) {
      case 'double': return 2;
      case 'triple': return 3;
      case 'quad': return 4;
      case 'sharing': return 'Any';
      default: return 1;
    }
  };

  const lowestPrice = getLowestPrice();
  const nights = calculateNights();

  return (
    <>
      <style>{`
        @media (min-width: 768px) {
          .route-nowrap-md {
            white-space: nowrap !important;
          }
        }
      `}</style>
      <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px', minHeight: '200px' }}>
        <Card.Body className="p-3">
        <Row className="align-items-center h-100">
          {/* Left Section - Package Info */}
          <Col lg={8}>
            <Row className="align-items-center">
              {/* Package Title & Key Info */}
              <Col md={12} className="mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h5 className="mb-1 fontweight700 text-dark">{pkg.title}</h5>
                    <div className="d-flex align-items-center flex-wrap gap-3 justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-calendar text-primary me-2"></i>
                        <div>
                          <div className="fontsize12 text-muted">Travel Dates</div>
                          <div className="fontsize15 fontweight700 text-dark">
                            {(() => {
                              const departureFlight = getFlightInfo(pkg.departure_flights);
                              const returnFlight = getFlightInfo(pkg.return_flights);
                              if (departureFlight && returnFlight) {
                                return `${formatDate(departureFlight.departure_date)} To ${formatDate(returnFlight.arrival_date)}`;
                              }
                              return 'Dates not available';
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-moon text-warning me-2"></i>
                        <div>
                          <div className="fontsize12 text-muted">Duration</div>
                          <div className="fontsize14 fontweight600">{nights} Nights</div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-users text-info me-2"></i>
                        <div>
                          <div className="fontsize12 text-muted">Seats</div>
                          <div className="fontsize14 fontweight600">
                            {pkg.available_seats || 0}/{pkg.total_seats || 0}
                          </div>
                        </div>
                      </div>
                      <Badge bg={pkg.available_seats > 0 ? "success" : "danger"} className="fontsize11 px-2 py-1">
                        {pkg.available_seats > 0 ? "Available" : "Fully Booked"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Flight Information - Compact */}
              <Col md={12}>
                <div className="row g-2">
                  {/* Departure Flight */}
                  <div className="col-md-6">
                    <div className="flight-info-compact p-2" style={{ 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px', 
                      border: '1px solid #e9ecef' 
                    }}>
                      <div className="row g-2">
                        {/* Column 1: Departure Info & Route */}
                        <div className="col-12 col-md-7">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-plane-departure text-info me-2" style={{ fontSize: '12px' }}></i>
                            <span className="fontsize12 fontweight600 text-dark">Departure</span>
                          </div>
                          {(() => {
                            const departureFlights = pkg.departure_flights || [];
                            if (departureFlights.length > 0) {
                              const segments = getAllFlightSegments(departureFlights);
                              if (segments.length === 1) {
                                const segment = segments[0];
                                return (
                                  <div className="fontsize13 fontweight600 text-dark">
                                    {segment.departure_airport} → {segment.arrival_airport}
                                  </div>
                                );
                              } else {
                                return (
                                  <div>
                                    <div className="fontsize13 fontweight600 text-dark route-nowrap-md">
                                      {formatFlightRoute(departureFlights)}
                                    </div>
                                    <div className="mt-1">
                                      <small className="text-info">
                                        <i className="fas fa-route me-1"></i>
                                        Via {segments.length - 1} stop{segments.length - 1 > 1 ? 's' : ''}
                                      </small>
                                    </div>
                                  </div>
                                );
                              }
                            }
                            return <div className="fontsize13 fontweight600 text-muted">Route not available</div>;
                          })()}
                        </div>

                        {/* Column 2: Airline Logo/Name */}
                        <div className="col-12 col-md-5">
                          {(() => {
                            const departureFlights = pkg.departure_flights || [];
                            if (departureFlights.length > 0) {
                              const segments = getAllFlightSegments(departureFlights);
                              const firstSegment = segments[0];
                              if (firstSegment.airline_id && airlineDataLoaded) {
                                const airlineLogo = getAirlineLogo(firstSegment.airline_id);
                                const airlineName = getAirlineName(firstSegment.airline_id);
                                const logoFailed = logoLoadStates[firstSegment.airline_id] === 'failed';
                                const shouldShowLogo = airlineLogo && !logoFailed;
                                
                                return (
                                  <div className="d-flex align-items-center justify-content-start justify-content-md-end">
                                    {shouldShowLogo ? (
                                      <img 
                                        src={airlineLogo} 
                                        alt="Airline Logo" 
                                        style={{ 
                                          height: '50px', 
                                          width: 'auto', 
                                          maxWidth: '100px',
                                          objectFit: 'contain'
                                        }}
                                        onError={() => handleLogoError(firstSegment.airline_id)}
                                      />
                                    ) : (
                                      <span className="fontsize11 fontweight600 text-primary text-start text-md-end">
                                        {airlineName}
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                            }
                            // Show loading state if data is not loaded yet
                            if (!airlineDataLoaded) {
                              return (
                                <div className="d-flex align-items-center">
                                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  <span className="fontsize11 text-muted">Loading...</span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                      {(() => {
                        const departureFlights = pkg.departure_flights || [];
                        if (departureFlights.length > 0) {
                          const segments = getAllFlightSegments(departureFlights);
                          
                          // If direct flight (1 segment), show as before
                          if (segments.length === 1) {
                            const segment = segments[0];
                            return (
                              <div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="d-flex align-items-center">
                                      <div className="fontsize11 text-muted">Flight</div>
                                    </div>
                                    <div className="fontsize12 fontweight600">{segment.flight_number}</div>
                                  </div>
                                  <div>
                                    <div className="fontsize11 text-muted">Date</div>
                                    <div className="fontsize12 fontweight600">{formatDateShort(segment.departure_date)}</div>
                                  </div>
                                  <div>
                                    <div className="fontsize11 text-muted">Time</div>
                                    <div className="fontsize12 fontweight600">{segment.departure_time}</div>
                                  </div>
                                  <div>
                                    <div className="fontsize11 text-muted">Arrival</div>
                                    <div className="fontsize12 fontweight600">{segment.arrival_time}</div>
                                  </div>
                                </div>
                                {/* Meals Info */}
                                <div className="mt-2">
                                  {segment.meals_included ? (
                                    <div className="d-flex align-items-center">
                                      <i className="fas fa-utensils text-success me-1" style={{ fontSize: '10px' }}></i>
                                      <small className="text-success fontsize10">Meals</small>
                                    </div>
                                  ) : (
                                    <div className="fontsize10 text-muted">No meals</div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          // If via flight (multiple segments), show each segment
                          return (
                            <div>
                              {segments.map((segment, idx) => (
                                <div key={idx} className="mb-2 p-2" style={{ 
                                  backgroundColor: '#ffffff', 
                                  borderRadius: '4px', 
                                  border: '1px solid #dee2e6' 
                                }}>
                                  <div className="fontsize13 fontweight600 mb-1">
                                    {segment.departure_airport} → {segment.arrival_airport}
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <div className="d-flex align-items-center">
                                        <div className="fontsize11 text-muted">Flight</div>

                                      </div>
                                      <div className="fontsize12 fontweight600">{segment.flight_number}</div>
                                    </div>
                                    <div>
                                      <div className="fontsize11 text-muted">Date</div>
                                      <div className="fontsize12 fontweight600">{formatDateShort(segment.departure_date)}</div>
                                    </div>
                                    <div>
                                      <div className="fontsize11 text-muted">Time</div>
                                      <div className="fontsize12 fontweight600">{segment.departure_time}</div>
                                    </div>
                                    <div>
                                      <div className="fontsize11 text-muted">Arrival</div>
                                      <div className="fontsize12 fontweight600">{segment.arrival_time}</div>
                                    </div>
                                  </div>
                                  {/* Meals Info */}
                                  <div className="mt-2">
                                    {segment.meals_included ? (
                                      <div className="d-flex align-items-center">
                                        <i className="fas fa-utensils text-success me-1" style={{ fontSize: '10px' }}></i>
                                        <small className="text-success fontsize10">Meals</small>
                                      </div>
                                    ) : (
                                      <div className="fontsize10 text-muted">No meals</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return <div className="fontsize12 text-muted">Departure info not available</div>;
                      })()}
                    </div>
                  </div>

                  {/* Return Flight */}
                  <div className="col-md-6">
                    <div className="flight-info-compact p-2" style={{ 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px', 
                      border: '1px solid #e9ecef' 
                    }}>
                      <div className="row g-2">
                        {/* Column 1: Return Info & Route */}
                        <div className="col-12 col-md-7">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-plane-arrival text-success me-2" style={{ fontSize: '12px' }}></i>
                            <span className="fontsize12 fontweight600 text-dark">Return</span>
                          </div>
                          {(() => {
                            const returnFlights = pkg.return_flights || [];
                            if (returnFlights.length > 0) {
                              const segments = getAllFlightSegments(returnFlights);
                              if (segments.length === 1) {
                                const segment = segments[0];
                                return (
                                  <div className="fontsize13 fontweight600 text-dark">
                                    {segment.departure_airport} → {segment.arrival_airport}
                                  </div>
                                );
                              } else {
                                return (
                                  <div>
                                    <div className="fontsize13 fontweight600 text-dark route-nowrap-md">
                                      {formatFlightRoute(returnFlights)}
                                    </div>
                                    <div className="mt-1">
                                      <small className="text-info">
                                        <i className="fas fa-route me-1"></i>
                                        Via {segments.length - 1} stop{segments.length - 1 > 1 ? 's' : ''}
                                      </small>
                                    </div>
                                  </div>
                                );
                              }
                            }
                            return <div className="fontsize13 fontweight600 text-muted">Route not available</div>;
                          })()}
                        </div>

                        {/* Column 2: Airline Logo/Name */}
                        <div className="col-12 col-md-5">
                          {(() => {
                            const returnFlights = pkg.return_flights || [];
                            if (returnFlights.length > 0) {
                              const segments = getAllFlightSegments(returnFlights);
                              const firstSegment = segments[0];
                              if (firstSegment.airline_id && airlineDataLoaded) {
                                const airlineLogo = getAirlineLogo(firstSegment.airline_id);
                                const airlineName = getAirlineName(firstSegment.airline_id);
                                const logoFailed = logoLoadStates[firstSegment.airline_id] === 'failed';
                                const shouldShowLogo = airlineLogo && !logoFailed;
                                
                                return (
                                  <div className="d-flex align-items-center justify-content-start justify-content-md-end">
                                    {shouldShowLogo ? (
                                      <img 
                                        src={airlineLogo} 
                                        alt="Airline Logo" 
                                        style={{ 
                                          height: '50px', 
                                          width: 'auto', 
                                          maxWidth: '100px',
                                          objectFit: 'contain'
                                        }}
                                        onError={() => handleLogoError(firstSegment.airline_id)}
                                      />
                                    ) : (
                                      <span className="fontsize11 fontweight600 text-primary text-start text-md-end">
                                        {airlineName}
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                            }
                            // Show loading state if data is not loaded yet
                            if (!airlineDataLoaded) {
                              return (
                                <div className="d-flex align-items-center">
                                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  <span className="fontsize11 text-muted">Loading...</span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                      {(() => {
                        const returnFlights = pkg.return_flights || [];
                        if (returnFlights.length > 0) {
                          const segments = getAllFlightSegments(returnFlights);
                          
                          // If direct flight (1 segment), show as before
                          if (segments.length === 1) {
                            const segment = segments[0];
                            return (
                              <div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="d-flex align-items-center">
                                      <div className="fontsize11 text-muted">Flight</div>
                                    </div>
                                    <div className="fontsize12 fontweight600">{segment.flight_number}</div>
                                  </div>
                                  <div>
                                    <div className="fontsize11 text-muted">Date</div>
                                    <div className="fontsize12 fontweight600">{formatDateShort(segment.departure_date)}</div>
                                  </div>
                                  <div>
                                    <div className="fontsize11 text-muted">Time</div>
                                    <div className="fontsize12 fontweight600">{segment.departure_time}</div>
                                  </div>
                                  <div>
                                    <div className="fontsize11 text-muted">Arrival</div>
                                    <div className="fontsize12 fontweight600">{segment.arrival_time}</div>
                                  </div>
                                </div>
                                {/* Meals Info */}
                                <div className="mt-2">
                                  {segment.meals_included ? (
                                    <div className="d-flex align-items-center">
                                      <i className="fas fa-utensils text-success me-1" style={{ fontSize: '10px' }}></i>
                                      <small className="text-success fontsize10">Meals</small>
                                    </div>
                                  ) : (
                                    <div className="fontsize10 text-muted">No meals</div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          // If via flight (multiple segments), show each segment
                          return (
                            <div>
                              {segments.map((segment, idx) => (
                                <div key={idx} className="mb-2 p-2" style={{ 
                                  backgroundColor: '#ffffff', 
                                  borderRadius: '4px', 
                                  border: '1px solid #dee2e6' 
                                }}>
                                  <div className="fontsize13 fontweight600 mb-1">
                                    {segment.departure_airport} → {segment.arrival_airport}
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <div className="d-flex align-items-center">
                                        <div className="fontsize11 text-muted">Flight</div>

                                      </div>
                                      <div className="fontsize12 fontweight600">{segment.flight_number}</div>
                                    </div>
                                    <div>
                                      <div className="fontsize11 text-muted">Date</div>
                                      <div className="fontsize12 fontweight600">{formatDateShort(segment.departure_date)}</div>
                                    </div>
                                    <div>
                                      <div className="fontsize11 text-muted">Time</div>
                                      <div className="fontsize12 fontweight600">{segment.departure_time}</div>
                                    </div>
                                    <div>
                                      <div className="fontsize11 text-muted">Arrival</div>
                                      <div className="fontsize12 fontweight600">{segment.arrival_time}</div>
                                    </div>
                                  </div>
                                  {/* Meals Info */}
                                  <div className="mt-2">
                                    {segment.meals_included ? (
                                      <div className="d-flex align-items-center">
                                        <i className="fas fa-utensils text-success me-1" style={{ fontSize: '10px' }}></i>
                                        <small className="text-success fontsize10">Meals</small>
                                      </div>
                                    ) : (
                                      <div className="fontsize10 text-muted">No meals</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return <div className="fontsize12 text-muted">Return info not available</div>;
                      })()}
                    </div>
                  </div>
                </div>
              </Col>

              {/* Hotel Information - Horizontal */}
              <Col md={12} className="mt-2">
                {pkg.hotels && pkg.hotels.length > 0 && (
                  <div className="hotel-info-horizontal p-3" style={{ 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px', 
                    border: '1px solid #e9ecef' 
                  }}>
                    
                    <div className="row g-3">
                      {pkg.hotels.map((hotel, idx) => (
                        <div key={idx} className="col-md-6">
                          <div className="d-flex align-items-start">
                            <i className={`fas ${hotel.city === 'Makkah' || hotel.city === 'makkah' || hotel.location?.toLowerCase().includes('makkah') ? 'fa-kaaba text-success' : 'fa-mosque text-info'} me-2 mt-1`} style={{ fontSize: '12px' }}></i>
                            <div className="flex-grow-1">
                              <div className="d-flex flex-column flex-lg-row flex-lg-wrap align-items-lg-center mb-2">
                                <div className="d-flex align-items-center mb-1 mb-lg-0">
                                  <span className="fontsize13 fontweight600 text-dark">
                                    {hotel.city === 'Makkah' || hotel.city === 'makkah' || hotel.location?.toLowerCase().includes('makkah') ? 'Makkah Hotel' : 
                                     hotel.city === 'Madina' || hotel.city === 'madina' || hotel.location?.toLowerCase().includes('madina') ? 'Madinah Hotel' : 'Hotel'}
                                  </span>
                                  <span className="fontsize13 text-muted mx-2 d-none d-lg-inline">-</span>
                                </div>
                                <span className="fontsize14 fontweight600 text-dark">
                                  {hotel.hotel_name || hotel.name || 'Hotel Name Not Available'}
                                </span>
                              </div>
                              {hotel.distance && (
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-walking text-info me-2" style={{ fontSize: '10px' }}></i>
                                  <span className="fontsize11 text-muted">
                                    {hotel.distance} from {hotel.city === 'Makkah' || hotel.city === 'makkah' || hotel.location?.toLowerCase().includes('makkah') ? 'Haram' : 'Haram'}
                                  </span>
                                </div>
                              )}
                              {hotel.address && (
                                <div className="d-flex align-items-start">
                                  <i className="fas fa-home text-secondary me-2 mt-1" style={{ fontSize: '10px' }}></i>
                                  <span className="fontsize11 text-muted">{hotel.address}</span>
                                </div>
                              )}
                              {hotel.location && !hotel.address && (
                                <div className="d-flex align-items-start">
                                  <i className="fas fa-map-marker-alt text-danger me-2 mt-1" style={{ fontSize: '10px' }}></i>
                                  <span className="fontsize11 text-muted">{hotel.location}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Col>

              {/* Package Features - Compact */}
              <Col md={12} className="mt-2">
                <div className="d-flex align-items-center flex-wrap gap-1">
                  {pkg.non_refundable === 1 && (
                    <Badge bg="warning" className="fontsize10 px-2 py-1">
                      <i className="fas fa-ban me-1"></i>Non-Refundable
                    </Badge>
                  )}
                  {pkg.non_changeable === 1 && (
                    <Badge bg="danger" className="fontsize10 px-2 py-1">
                      <i className="fas fa-ban me-1"></i>Non Changeable
                    </Badge>
                  )}
                  {pkg.zayarat_included === 1 && (
                    <Badge bg="warning" className="fontsize10 px-2 py-1">
                      <i className="fas fa-mosque me-1"></i>Zayarat
                    </Badge>
                  )}
                </div>
              </Col>
            </Row>
          </Col>

          {/* Right Section - Pricing & Booking */}
          <Col lg={4}>
            <div className="text-center">
              {/* Pricing Information */}
              <div className="mb-3">
                <div className="fontsize12 text-muted">Starting from</div>
                {lowestPrice && (
                  <h4 className="mb-0 text-success fontweight700">
                    PKR {lowestPrice.toLocaleString()}
                  </h4>
                )}
                <small className="text-muted">per person</small>
                
                {/* Discount Badge */}
                {(pkg.discount_adult || pkg.discount_child || pkg.discount_infant) && (
                  <div className="mt-2">
                    <Badge bg="warning" className="px-2 py-1">
                      <i className="fas fa-tag me-1"></i>
                      Discounts Available
                    </Badge>
                  </div>
                )}
              </div>
              
              {/* Room Type Buttons - Compact */}
              <div className="mb-3">
                <Row className="g-1">
                  {pkg.prices && pkg.prices
                    .filter(p => (p.price_adult && p.price_adult > 0) || (p.price_child && p.price_child > 0) || (p.price_infant && p.price_infant > 0))
                    .slice(0, 4) // Limit to 4 room types for compact display
                    .reverse() // Reverse the order to show QUINT/SHARING, QUAD, TRIPLE, DOUBLE
                    .map((price, idx) => {
                      const capacity = getRoomCapacity(price.room_type);
                      const requiredSeats = capacity === 'Any' ? 1 : capacity;
                      const isAvailable = pkg.available_seats >= requiredSeats;
                      
                      return (
                      <Col key={idx} xs={6}>
                        <Button
                          variant={isAvailable ? "outline-success" : "outline-secondary"}
                          size="sm"
                          className={`w-100 py-2 ${!isAvailable ? 'opacity-50' : ''}`}
                          style={{ 
                            fontSize: '10px', 
                            borderRadius: '4px',
                            cursor: isAvailable ? 'pointer' : 'not-allowed'
                          }}
                          onClick={() => {
                            if (isAvailable) {
                              onBook({ 
                                ...pkg, 
                                selectedRoomType: price.room_type, 
                                selectedPricing: price, 
                                bookingType: 'room_specific'
                              });
                            } else {
                              const toast = require('react-hot-toast');
                              toast.error(`Only ${pkg.available_seats} seat(s) available. ${price.room_type.toUpperCase()} room requires ${requiredSeats} people.`);
                            }
                          }}
                          disabled={!isAvailable}
                        >
                          <div className="d-flex flex-column align-items-center">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-bed me-1" style={{ fontSize: '10px', color: 'var(--bs-success)' }}></i>
                              <span className="fontweight600" style={{ fontSize: '11px' }}>
                                {(price.room_type === "sharing" ? "QUINT/SHARING" : price.room_type.toUpperCase())}
                              </span>
                            </div>
                            <div className="fontsize10 mb-1" style={{ color: 'var(--bs-secondary)' }}>
                              {getRoomCapacity(price.room_type)} {getRoomCapacity(price.room_type) === 'Any' ? 'people' : 'people'} / room
                            </div>
                            {price.price_adult && price.price_adult > 0 && (
                              <div className="text-center">
                                <div className="fontweight700" style={{ fontSize: '12px', color: 'var(--bs-success)' }}>
                                  PKR {Number(price.price_adult).toLocaleString()}
                                </div>
                                <div className="fontsize9" style={{ color: 'var(--bs-secondary)' }}>per adult</div>
                              </div>
                            )}
                          </div>
                        </Button>
                        <style jsx>{`
                          .btn-outline-success:hover .fas,
                          .btn-outline-success:hover .fontsize10,
                          .btn-outline-success:hover .fontsize9,
                          .btn-outline-success:hover .fontweight700 {
                            color: white !important;
                          }
                        `}</style>
                      </Col>
                      );
                    })
                  }
                </Row>
              </div>
              
              {/* General Booking Button */}
              <Button
                variant="success"
                className="w-100 py-2 fontweight600"
                style={{ borderRadius: '6px', fontSize: '13px' }}
                onClick={() => onBook({ ...pkg, bookingType: 'general' })}
              >
                <i className="fas fa-calendar-check me-2"></i>
                BOOK NOW
              </Button>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
    </>
  );
};

export default UmrahPackageCard;